export type ServiceStatus = 'online' | 'degraded' | 'offline' | 'maintenance';

export interface ServiceIncident {
  id: string;
  date: string;
  title: string;
  description: string;
  severity: 'minor' | 'major' | 'critical';
  resolved: boolean;
  duration: string;
}

export interface ServiceAvailabilityPoint {
  date: string;
  value: number;
}

export interface SystemHealthService {
  id: string;
  name: string;
  icon: string;
  category: 'infrastructure' | 'streaming' | 'payments' | 'notifications' | 'ai' | 'integrations' | 'external';
  status: ServiceStatus;
  availability: number;
  responseTime: number;
  lastChecked: string;
  version: string;
  recentErrors: number;
  incidents: ServiceIncident[];
  availabilityHistory: ServiceAvailabilityPoint[];
  description: string;
  endpoint: string;
}

export const statusMeta: Record<ServiceStatus, { label: string; color: string; bg: string; dot: string; border: string }> = {
  online: {
    label: 'Operativo',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    dot: 'bg-emerald-500',
    border: 'border-emerald-200',
  },
  degraded: {
    label: 'Degradado',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    dot: 'bg-amber-500',
    border: 'border-amber-200',
  },
  offline: {
    label: 'Caído',
    color: 'text-red-600',
    bg: 'bg-red-50',
    dot: 'bg-red-500',
    border: 'border-red-200',
  },
  maintenance: {
    label: 'Mantenimiento',
    color: 'text-sky-600',
    bg: 'bg-sky-50',
    dot: 'bg-sky-500',
    border: 'border-sky-200',
  },
};

export const severityMeta: Record<string, { label: string; color: string; bg: string }> = {
  minor: { label: 'Menor', color: 'text-amber-600', bg: 'bg-amber-50' },
  major: { label: 'Mayor', color: 'text-orange-600', bg: 'bg-orange-50' },
  critical: { label: 'Crítico', color: 'text-red-600', bg: 'bg-red-50' },
};

function generateAvailabilityHistory(baseValue: number, dips: { day: number; value: number }[]): ServiceAvailabilityPoint[] {
  const points: ServiceAvailabilityPoint[] = [];
  const now = new Date('2026-07-12');
  for (let i = 29; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().split('T')[0];
    let value = baseValue + (Math.random() - 0.5) * 1.5;
    const dip = dips.find((dip) => {
      const dipDate = new Date(now);
      dipDate.setDate(dipDate.getDate() - dip.day);
      return dipDate.toISOString().split('T')[0] === dateStr;
    });
    if (dip) value = dip.value;
    value = Math.min(100, Math.max(0, Math.round(value * 100) / 100));
    points.push({ date: dateStr, value });
  }
  return points;
}

export const systemHealthServices: SystemHealthService[] = [
  {
    id: 'supabase',
    name: 'Supabase',
    icon: 'ri-database-2-line',
    category: 'infrastructure',
    status: 'online',
    availability: 99.97,
    responseTime: 45,
    lastChecked: '2026-07-12T17:45:00Z',
    version: 'v2.1.8',
    recentErrors: 0,
    description: 'Backend como servicio. Base de datos PostgreSQL, autenticación, storage y edge functions.',
    endpoint: 'https://api.readdy.ai',
    incidents: [],
    availabilityHistory: generateAvailabilityHistory(99.98, []),
  },
  {
    id: 'api',
    name: 'API Gateway',
    icon: 'ri-terminal-box-line',
    category: 'infrastructure',
    status: 'online',
    availability: 99.95,
    responseTime: 32,
    lastChecked: '2026-07-12T17:45:00Z',
    version: 'v3.4.2',
    recentErrors: 2,
    description: 'API Gateway principal. Enruta todas las peticiones del frontend hacia los microservicios internos.',
    endpoint: 'https://api.readdy.ai/v3',
    incidents: [
      {
        id: 'INC-API-001',
        date: '2026-07-11T14:22:00Z',
        title: 'Latencia elevada en API Gateway',
        description: 'Picos de latencia de hasta 2.3s detectados entre 14:22 y 14:48 UTC. Causa: saturación de pool de conexiones por despliegue simultáneo de 3 microservicios.',
        severity: 'minor',
        resolved: true,
        duration: '26 min',
      },
    ],
    availabilityHistory: generateAvailabilityHistory(99.96, [{ day: 1, value: 99.12 }]),
  },
  {
    id: 'streaming',
    name: 'Streaming Engine',
    icon: 'ri-live-line',
    category: 'streaming',
    status: 'online',
    availability: 99.89,
    responseTime: 28,
    lastChecked: '2026-07-12T17:45:00Z',
    version: 'v5.2.0',
    recentErrors: 1,
    description: 'Motor de streaming de audio. Gestiona todos los flujos de audio en vivo para las radios de la plataforma.',
    endpoint: 'https://stream.readdy.ai',
    incidents: [
      {
        id: 'INC-STREAM-001',
        date: '2026-07-10T03:15:00Z',
        title: 'Interrupción en nodo de streaming US-East',
        description: 'Caída del nodo US-East por sobrecarga de memoria. 12 radios afectadas durante 8 minutos. Failover automático al nodo US-West.',
        severity: 'major',
        resolved: true,
        duration: '8 min',
      },
    ],
    availabilityHistory: generateAvailabilityHistory(99.90, [{ day: 2, value: 98.45 }]),
  },
  {
    id: 'smtp',
    name: 'SMTP / Email',
    icon: 'ri-mail-send-line',
    category: 'infrastructure',
    status: 'online',
    availability: 99.99,
    responseTime: 210,
    lastChecked: '2026-07-12T17:45:00Z',
    version: 'Resend v1.2',
    recentErrors: 0,
    description: 'Servicio de envío de correos transaccionales, notificaciones y emails de marketing.',
    endpoint: 'smtp.resend.com',
    incidents: [],
    availabilityHistory: generateAvailabilityHistory(99.99, []),
  },
  {
    id: 'storage',
    name: 'Storage / CDN',
    icon: 'ri-hard-drive-2-line',
    category: 'infrastructure',
    status: 'degraded',
    availability: 99.74,
    responseTime: 85,
    lastChecked: '2026-07-12T17:45:00Z',
    version: 'Supabase Storage v2.1',
    recentErrors: 5,
    description: 'Almacenamiento de archivos, imágenes, audio y video. Servido a través de CDN global.',
    endpoint: 'https://storage.readdy.ai',
    incidents: [
      {
        id: 'INC-STG-001',
        date: '2026-07-12T08:30:00Z',
        title: 'Latencia elevada en Storage',
        description: 'Aumento de latencia de 85ms a 340ms en el clúster de storage. Causa: replicación de datos entre nodos por actualización programada.',
        severity: 'minor',
        resolved: false,
        duration: 'En curso',
      },
      {
        id: 'INC-STG-002',
        date: '2026-07-08T22:10:00Z',
        title: 'Error 503 en CDN Edge Nodes',
        description: 'Varios nodos CDN edge reportaron error 503 durante 15 minutos. Causa: actualización de configuración de cache con parámetro inválido.',
        severity: 'major',
        resolved: true,
        duration: '15 min',
      },
    ],
    availabilityHistory: generateAvailabilityHistory(99.78, [{ day: 0, value: 99.42 }, { day: 4, value: 98.91 }]),
  },
  {
    id: 'cdn',
    name: 'Cloudflare CDN',
    icon: 'ri-cloud-line',
    category: 'infrastructure',
    status: 'online',
    availability: 99.99,
    responseTime: 12,
    lastChecked: '2026-07-12T17:45:00Z',
    version: 'Enterprise',
    recentErrors: 0,
    description: 'CDN global a través de Cloudflare Enterprise. Distribución de contenido estático, DDoS protection y WAF.',
    endpoint: 'https://cdn.readdy.ai',
    incidents: [],
    availabilityHistory: generateAvailabilityHistory(99.99, []),
  },
  {
    id: 'onesignal',
    name: 'OneSignal',
    icon: 'ri-notification-4-line',
    category: 'notifications',
    status: 'online',
    availability: 99.92,
    responseTime: 95,
    lastChecked: '2026-07-12T17:45:00Z',
    version: 'API v3',
    recentErrors: 1,
    description: 'Plataforma de push notifications para web, iOS y Android. Notificaciones en tiempo real para oyentes y administradores.',
    endpoint: 'https://onesignal.com/api/v3',
    incidents: [
      {
        id: 'INC-ONS-001',
        date: '2026-07-09T16:45:00Z',
        title: 'Retraso en entrega de push notifications',
        description: 'Retraso de hasta 4 minutos en la entrega de notificaciones push a dispositivos Android. Problema reportado por OneSignal en su status page.',
        severity: 'minor',
        resolved: true,
        duration: '42 min',
      },
    ],
    availabilityHistory: generateAvailabilityHistory(99.93, [{ day: 3, value: 99.15 }]),
  },
  {
    id: 'firebase',
    name: 'Firebase',
    icon: 'ri-fire-line',
    category: 'notifications',
    status: 'online',
    availability: 99.96,
    responseTime: 55,
    lastChecked: '2026-07-12T17:45:00Z',
    version: 'SDK 10.12',
    recentErrors: 0,
    description: 'Analytics, Crashlytics, y Cloud Messaging (FCM) para apps móviles de la plataforma.',
    endpoint: 'https://firebase.google.com',
    incidents: [],
    availabilityHistory: generateAvailabilityHistory(99.97, []),
  },
  {
    id: 'meta',
    name: 'Meta / Facebook',
    icon: 'ri-meta-line',
    category: 'integrations',
    status: 'online',
    availability: 99.88,
    responseTime: 180,
    lastChecked: '2026-07-12T17:45:00Z',
    version: 'Graph API v19',
    recentErrors: 3,
    description: 'Integración con Facebook e Instagram. Login social, compartir contenido y sincronización de perfiles.',
    endpoint: 'https://graph.facebook.com/v19.0',
    incidents: [
      {
        id: 'INC-META-001',
        date: '2026-07-07T11:30:00Z',
        title: 'Error de autenticación OAuth',
        description: 'Fallas intermitentes en login con Facebook. Tokens de acceso expirando prematuramente. Relacionado con cambio en políticas de Meta.',
        severity: 'minor',
        resolved: true,
        duration: '1h 15min',
      },
    ],
    availabilityHistory: generateAvailabilityHistory(99.89, [{ day: 5, value: 98.72 }]),
  },
  {
    id: 'stripe',
    name: 'Stripe',
    icon: 'ri-bank-card-line',
    category: 'payments',
    status: 'online',
    availability: 99.99,
    responseTime: 120,
    lastChecked: '2026-07-12T17:45:00Z',
    version: 'API 2024-06',
    recentErrors: 0,
    description: 'Procesador de pagos principal. Suscripciones, pagos únicos, facturación automática y gestión de tarjetas.',
    endpoint: 'https://api.stripe.com/v1',
    incidents: [],
    availabilityHistory: generateAvailabilityHistory(99.99, []),
  },
  {
    id: 'paypal',
    name: 'PayPal',
    icon: 'ri-paypal-line',
    category: 'payments',
    status: 'online',
    availability: 99.95,
    responseTime: 350,
    lastChecked: '2026-07-12T17:45:00Z',
    version: 'REST API v2',
    recentErrors: 0,
    description: 'Método de pago alternativo. Procesamiento de pagos vía PayPal y verificación de cuentas.',
    endpoint: 'https://api-m.paypal.com/v2',
    incidents: [],
    availabilityHistory: generateAvailabilityHistory(99.96, []),
  },
  {
    id: 'shopify',
    name: 'Shopify',
    icon: 'ri-shopping-bag-3-line',
    category: 'integrations',
    status: 'maintenance',
    availability: 99.82,
    responseTime: 155,
    lastChecked: '2026-07-12T17:45:00Z',
    version: 'Storefront API 2024-07',
    recentErrors: 0,
    description: 'Integración con tiendas Shopify. Sincronización de productos, inventario y gestión de pedidos en el marketplace.',
    endpoint: 'https://readdy.myshopify.com/api/2024-07',
    incidents: [
      {
        id: 'INC-SHOP-001',
        date: '2026-07-12T15:00:00Z',
        title: 'Mantenimiento programado',
        description: 'Actualización de versión de Storefront API de 2024-04 a 2024-07. Posibles interrupciones breves durante la migración.',
        severity: 'minor',
        resolved: false,
        duration: 'En curso',
      },
    ],
    availabilityHistory: generateAvailabilityHistory(99.88, [{ day: 0, value: 99.45 }]),
  },
  {
    id: 'centova',
    name: 'CentovaCast',
    icon: 'ri-broadcast-line',
    category: 'streaming',
    status: 'online',
    availability: 99.91,
    responseTime: 40,
    lastChecked: '2026-07-12T17:45:00Z',
    version: 'v3.4.2',
    recentErrors: 0,
    description: 'Panel de control de streaming. AutoDJ, estadísticas de oyentes y gestión de encoding para radios que usan CentovaCast.',
    endpoint: 'https://centovacast.readdy.ai',
    incidents: [],
    availabilityHistory: generateAvailabilityHistory(99.92, []),
  },
  {
    id: 'azura',
    name: 'AzuraCast',
    icon: 'ri-radio-line',
    category: 'streaming',
    status: 'online',
    availability: 99.87,
    responseTime: 52,
    lastChecked: '2026-07-12T17:45:00Z',
    version: 'v0.20.3',
    recentErrors: 2,
    description: 'Plataforma de gestión de radio web. AutoDJ, programación, relays y estadísticas para radios que usan AzuraCast.',
    endpoint: 'https://azuracast.readdy.ai',
    incidents: [
      {
        id: 'INC-AZURA-001',
        date: '2026-07-06T19:00:00Z',
        title: 'Fallo en Liquidsoap',
        description: 'Error en el procesador de audio Liquidsoap que causó silencio en 8 estaciones. Reinicio automático del servicio luego de 3 minutos.',
        severity: 'major',
        resolved: true,
        duration: '3 min',
      },
    ],
    availabilityHistory: generateAvailabilityHistory(99.89, [{ day: 6, value: 98.22 }]),
  },
  {
    id: 'openai',
    name: 'OpenAI',
    icon: 'ri-openai-line',
    category: 'ai',
    status: 'online',
    availability: 99.85,
    responseTime: 420,
    lastChecked: '2026-07-12T17:45:00Z',
    version: 'GPT-4o',
    recentErrors: 1,
    description: 'API de inteligencia artificial. Chat, embeddings, moderación de contenido, generación de texto y análisis semántico.',
    endpoint: 'https://api.openai.com/v1',
    incidents: [
      {
        id: 'INC-OAI-001',
        date: '2026-07-08T14:00:00Z',
        title: 'Rate limiting en API de OpenAI',
        description: 'Se alcanzó el límite de rate en tier 3. 23 solicitudes rechazadas en ventana de 1 minuto. Se implementó exponential backoff.',
        severity: 'minor',
        resolved: true,
        duration: '8 min',
      },
    ],
    availabilityHistory: generateAvailabilityHistory(99.86, [{ day: 4, value: 99.20 }]),
  },
  {
    id: 'gemini',
    name: 'Gemini',
    icon: 'ri-sparkling-2-line',
    category: 'ai',
    status: 'online',
    availability: 99.82,
    responseTime: 380,
    lastChecked: '2026-07-12T17:45:00Z',
    version: 'Gemini 1.5 Pro',
    recentErrors: 0,
    description: 'API de Google Gemini. Modelo multimodal para análisis de contenido, generación de descripciones y procesamiento de imágenes.',
    endpoint: 'https://generativelanguage.googleapis.com/v1beta',
    incidents: [],
    availabilityHistory: generateAvailabilityHistory(99.84, []),
  },
];

export interface GlobalStatusSnapshot {
  total: number;
  online: number;
  degraded: number;
  offline: number;
  maintenance: number;
  overallStatus: 'healthy' | 'warning' | 'critical';
  overallLabel: string;
}

export function computeGlobalStatus(services: SystemHealthService[]): GlobalStatusSnapshot {
  const total = services.length;
  const online = services.filter((s) => s.status === 'online').length;
  const degraded = services.filter((s) => s.status === 'degraded').length;
  const offline = services.filter((s) => s.status === 'offline').length;
  const maintenance = services.filter((s) => s.status === 'maintenance').length;

  let overallStatus: GlobalStatusSnapshot['overallStatus'] = 'healthy';
  let overallLabel = 'Todos los sistemas operativos';

  if (offline > 0) {
    overallStatus = 'critical';
    overallLabel = `${offline} servicio${offline > 1 ? 's' : ''} caído${offline > 1 ? 's' : ''}`;
  } else if (degraded > 0 || maintenance > 0) {
    overallStatus = 'warning';
    const parts: string[] = [];
    if (degraded > 0) parts.push(`${degraded} degradado${degraded > 1 ? 's' : ''}`);
    if (maintenance > 0) parts.push(`${maintenance} en mantenimiento`);
    overallLabel = parts.join(' · ');
  }

  return { total, online, degraded, offline, maintenance, overallStatus, overallLabel };
}

export const allIncidentsChronological: ServiceIncident[] = systemHealthServices
  .flatMap((s) => s.incidents.map((i) => ({ ...i, serviceId: s.id, serviceName: s.name, serviceIcon: s.icon } as ServiceIncident & { serviceId: string; serviceName: string; serviceIcon: string })))
  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

export const categoryLabels: Record<SystemHealthService['category'], string> = {
  infrastructure: 'Infraestructura',
  streaming: 'Streaming',
  payments: 'Pagos',
  notifications: 'Notificaciones',
  ai: 'Inteligencia Artificial',
  integrations: 'Integraciones',
  external: 'Externos',
};

export const categoryIcons: Record<SystemHealthService['category'], string> = {
  infrastructure: 'ri-server-line',
  streaming: 'ri-live-line',
  payments: 'ri-bank-card-line',
  notifications: 'ri-notification-3-line',
  ai: 'ri-brain-line',
  integrations: 'ri-plug-line',
  external: 'ri-external-link-line',
};